import { Link, useNavigate } from "react-router-dom";
import { Heart, ArrowRight, Sparkles, Shield } from "lucide-react";
import { useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { AUTH } from "@/constants/testIds";

export default function LoginPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user) navigate("/app", { replace: true });
  }, [user, loading, navigate]);

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <aside className="hidden lg:flex flex-col justify-between p-12 bg-[var(--fcc-ink)] text-white">
        <Link to="/" className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-white/10 flex items-center justify-center relative">
            <Heart className="w-4 h-4" />
            <span className="absolute -top-1 -right-1 w-2 h-2 bg-[var(--fcc-orange)] rounded-full" />
          </div>
          <div className="leading-tight">
            <div className="font-display text-[22px] tracking-tight">KidHQ</div>
            <div className="text-[10px] tracking-[0.22em] uppercase text-white/60">AI Command Center</div>
          </div>
        </Link>

        <div className="space-y-6">
          <h2 className="font-display text-4xl leading-[1.05]">
            The AI Command Center for busy parents.
          </h2>
          <p className="text-white/75 leading-relaxed max-w-md">
            One account. Every kid, every coach, every camp, every form. No more hunting through 47 emails to remember snack duty.
          </p>
          <ul className="space-y-3 text-sm text-white/80">
            <li className="flex gap-3"><Sparkles className="w-4 h-4 mt-0.5 text-[var(--fcc-peach)] shrink-0" /> Real Claude Sonnet 4.5 classification on every email you paste in.</li>
            <li className="flex gap-3"><Shield className="w-4 h-4 mt-0.5 text-[var(--fcc-peach)] shrink-0" /> Adult emails are filtered out. Your data isn't used to train models.</li>
          </ul>
        </div>
        <p className="text-xs text-white/40">kidhq.net · A product of <strong className="text-white/60">All IT Solutions LLC</strong> · Smart people, smart solutions</p>
      </aside>

      <main className="flex items-center justify-center p-8 sm:p-12 bg-[var(--fcc-cream)]">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden flex items-center gap-3 mb-10">
            <div className="w-10 h-10 rounded-2xl bg-[var(--fcc-ink)] text-white flex items-center justify-center relative">
              <Heart className="w-4 h-4" />
              <span className="absolute -top-1 -right-1 w-2 h-2 bg-[var(--fcc-orange)] rounded-full" />
            </div>
            <div className="font-display text-[22px] text-[var(--fcc-green)]">KidHQ</div>
          </Link>

          <p className="label-overline">Sign in</p>
          <h1 className="font-display text-[40px] sm:text-[52px] leading-[1.05] text-[var(--fcc-ink)] mt-3">
            Welcome to KidHQ.
          </h1>
          <p className="mt-4 text-[var(--fcc-ink-soft)] leading-relaxed">
            Sign in with the Google account where your kids' school, sports, and camp emails arrive.
          </p>

          <button
            data-testid={AUTH.googleSignIn}
            onClick={handleGoogle}
            className="mt-8 w-full bg-white border border-[var(--fcc-border)] rounded-2xl py-3.5 px-5 flex items-center justify-center gap-3 font-semibold text-[var(--fcc-ink)] hover:bg-[var(--fcc-cream-2)] transition"
          >
            <GoogleMark />
            Continue with Google
            <ArrowRight className="w-4 h-4 ml-1" />
          </button>

          <p className="mt-6 text-xs text-[var(--fcc-muted)] leading-relaxed">
            By signing in you agree to KidHQ's privacy practices. We only read child-related messages and we never train models on your data.
          </p>

          <div className="mt-10 fcc-card-accent p-5">
            <p className="label-overline">Coming next</p>
            <p className="text-sm text-[var(--fcc-ink)] mt-2 leading-relaxed">
              <strong>Gmail auto-ingestion</strong> ships in the next iteration. Today, sign in with Google for your KidHQ identity and use the <strong>Paste an email</strong> flow to see real Claude classification on your own messages.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}

function GoogleMark() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.4 29.3 35.5 24 35.5c-6.4 0-11.5-5.1-11.5-11.5S17.6 12.5 24 12.5c2.9 0 5.6 1.1 7.6 2.9l5.7-5.7C33.9 6.4 29.2 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24S13.2 43.5 24 43.5 43.5 34.8 43.5 24c0-1.2-.1-2.3-.4-3.5z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16 18.9 13 24 13c2.9 0 5.6 1.1 7.6 2.9l5.7-5.7C33.9 6.9 29.2 5 24 5 16.2 5 9.4 9.2 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 43c5.1 0 9.8-1.9 13.3-5.1l-6.1-5.2C29.2 34.3 26.7 35.5 24 35.5c-5.3 0-9.7-3.1-11.3-7.4l-6.5 5C9.3 38.7 16.1 43 24 43z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4-4 5.3l6.1 5.2C40.6 35.6 43.5 30.3 43.5 24c0-1.2-.1-2.3-.4-3.5z"/>
    </svg>
  );
}
